import { useLayoutEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Launcher V2 A2 U4 (R4) — auto-grows a `<textarea>` to fit its content,
 * re-measuring whenever `value` changes (typing, paste, or an external
 * reseed such as `useDraftState`'s `reseed`).
 *
 * Wraps Glaze's real `TextareaField`/`Textarea` rather than replacing it:
 * `TextareaField` forwards its ref straight through to the raw `<textarea>`
 * element (`textarea-field.tsx` → `textarea.tsx`'s `FieldPrimitive.Control`
 * ref), so this hook can drive `style.height` directly on that element
 * without needing its own textarea implementation. Pass `resizable={false}`
 * on the field — auto-grow and the manual `resize-y` drag handle fight each
 * other (both are trying to own `height`) — plus a `min-h-*` class so an
 * empty value doesn't collapse to a 0px sliver before the first measurement.
 *
 * DOM-measurement-only (`scrollHeight`), so it holds no lifetime ref state
 * of its own (no `mounted`/`initialized`/generation-counter ref) — every run
 * just re-measures against the current DOM node and is naturally
 * StrictMode-safe: a double-invoked effect just measures twice, harmlessly.
 *
 * ── CODEX HARDENING FIX — remount detection via `remountKey`: `ref`'s
 * IDENTITY is not a useful dependency for detecting a remount. `useRef`
 * always returns the same object across renders (that's the whole point of
 * a ref), so when `SequencesStep`'s `StepEditorShell` collapses and reopens —
 * unmounting and remounting the `<textarea>` DOM node, because Glaze's
 * `CollapsibleCard`/`Collapsible.Panel` defaults to `keepMounted=false` — the
 * effect's `[ref, value]` deps can both stay unchanged (the ref object is
 * the same object; the draft body text hasn't necessarily changed either),
 * so the effect does not re-run and the freshly-mounted node keeps whatever
 * `style.height` it happens to render with, never measured against its real
 * content. This is a distinct subtlety from the StrictMode-safety note
 * above: StrictMode's double-invoke is safe because re-running the effect on
 * the SAME node is harmless; this bug is about the effect NOT re-running on
 * a DIFFERENT (newly mounted) node because nothing told it to. The caller
 * passes a value that changes exactly on the closed→open transition (the
 * `open` boolean itself, e.g. `useAutosizeTextarea(bodyRef, value.body,
 * open)`) so the effect fires right when the new node has just mounted and
 * needs its first real measurement.
 *
 * No unit test: this is DOM-API-only (real `scrollHeight`/layout) and lives
 * outside `mini-apps/launcher`'s bun:test harness, which has no jsdom/
 * happy-dom (same constraint documented in `gong-flow-picker-api.ts`'s
 * header and `__tests__/v2-play-flow-shell.test.tsx`). Verified by Spencer's
 * browser QA instead.
 */
export function useAutosizeTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
  /**
   * Anything that changes exactly when the textarea's DOM node has just
   * been remounted (e.g. a `CollapsibleCard`'s `open` boolean) — see the
   * header comment. Optional: call sites whose textarea never unmounts
   * don't need to pass one.
   */
  remountKey?: unknown,
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [ref, value, remountKey]);
}
